import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'day_checkin_time', label: 'Day Check-In', type: 'time' },
  { key: 'day_checkout_time', label: 'Day Check-Out', type: 'time' },
];

const BREAKS = [
  { winKey: 'tea1_window', minKey: 'tea1_minutes', label: 'Tea 1st Half (morning)', defaultWin: '09:00-13:00' },
  { winKey: 'tea2_window', minKey: 'tea2_minutes', label: 'Tea 2nd Half (afternoon)', defaultWin: '14:00-17:00' },
  { winKey: 'tea_evening_window', minKey: 'tea_evening_minutes', label: 'Evening Tea', defaultWin: '17:00-20:00' },
  { winKey: 'lunch_window', minKey: 'lunch_minutes', label: 'Lunch', defaultWin: '12:30-15:00' },
];

export default function StaffScheduleModal({ staff, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setForm({
      day_checkin_time: staff.day_checkin_time || '09:00',
      day_checkout_time: staff.day_checkout_time || '20:00',
      tea1_window: staff.tea1_window || '09:00-13:00',
      tea1_minutes: staff.tea1_minutes ?? 15,
      tea2_window: staff.tea2_window || '14:00-17:00',
      tea2_minutes: staff.tea2_minutes ?? 15,
      tea_evening_window: staff.tea_evening_window || '17:00-20:00',
      tea_evening_minutes: staff.tea_evening_minutes ?? 15,
      lunch_window: staff.lunch_window || '12:30-15:00',
      lunch_minutes: staff.lunch_minutes ?? 60,
      permission_monthly_hours: staff.permission_monthly_hours ?? 3,
    });
  }, [staff]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const token = localStorage.getItem('token');
      const body = { ...form };
      // Coerce numbers
      ['tea1_minutes', 'tea2_minutes', 'tea_evening_minutes', 'lunch_minutes', 'permission_monthly_hours'].forEach(k => {
        if (body[k] !== '' && body[k] != null) body[k] = parseFloat(body[k]);
      });
      const r = await fetch(`${API_URL}/api/admin/staff/${staff.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Save failed');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="staff-schedule-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg">Schedule — {staff.name}</h2>
            <p className="text-xs text-blue-100">Day timing & break windows for this staff member</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded" data-testid="staff-schedule-close"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Day timings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Day Timings</h3>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(f => (
                <label key={f.key} className="block">
                  <span className="text-xs text-gray-600">{f.label}</span>
                  <input
                    type={f.type}
                    value={form[f.key] || ''}
                    onChange={(e) => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                    data-testid={`schedule-${f.key}`}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Break windows */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Break Windows & Allowances</h3>
            <p className="text-xs text-gray-500 mb-3">Use the format <code className="bg-gray-100 px-1 rounded">HH:MM-HH:MM</code> (24-hour, e.g. <code className="bg-gray-100 px-1 rounded">14:00-17:00</code>).</p>
            <div className="space-y-2">
              {BREAKS.map(b => (
                <div key={b.winKey} className="grid grid-cols-3 gap-2 items-center">
                  <span className="text-sm text-gray-700">{b.label}</span>
                  <input
                    type="text"
                    placeholder={b.defaultWin}
                    value={form[b.winKey] || ''}
                    onChange={(e) => setForm(s => ({ ...s, [b.winKey]: e.target.value }))}
                    className="px-3 py-2 border rounded-lg font-mono text-sm"
                    data-testid={`schedule-${b.winKey}`}
                  />
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form[b.minKey] ?? ''}
                      onChange={(e) => setForm(s => ({ ...s, [b.minKey]: e.target.value }))}
                      className="w-20 px-3 py-2 border rounded-lg"
                      data-testid={`schedule-${b.minKey}`}
                    />
                    <span className="text-xs text-gray-500">min</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Permission monthly */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">Monthly Permission Allowance</h3>
            <p className="text-xs text-amber-800 mb-2">Pro-rated by attendance — full allowance after 26 working days. (Worked 13 days → half allowance.)</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                min="0"
                value={form.permission_monthly_hours ?? ''}
                onChange={(e) => setForm(s => ({ ...s, permission_monthly_hours: e.target.value }))}
                className="w-24 px-3 py-2 border rounded-lg"
                data-testid="schedule-permission-monthly-hours"
              />
              <span className="text-sm text-amber-900">hours / month (full)</span>
            </div>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-3 flex justify-end gap-2 border-t">
          <button onClick={onClose} className="px-4 py-2 bg-white border rounded-lg text-sm" data-testid="staff-schedule-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="staff-schedule-save"
          >
            <Save size={14} /> {saving ? 'Saving…' : 'Save Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
